import { zodResolver } from "@hookform/resolvers/zod";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import { SubmitButton } from "@/components/common";
import { TimeStamp } from "@/models/timestamp";
import { Button } from "@/sx-layout/common/Button";
import { FormLabel, FormRow, InputText, TextArea, Radio, FileUploader, FormError } from "@/sx-layout/common/Form";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "@/sx-layout/common/Modal";
import { ToggleWithWords } from "@/sx-layout/common/ToggleWithWords";
import { PlotPlan, PlotPlanType, PlotPlanTypeType } from "@/sx-layout/components/settings/plotPlanSetting/models";
import { qualityMasters } from "@/sx-layout/components/settings/plotPlanSetting/utils/quality";
import { usePlotPlanApi } from "@/sx-layout/hooks/usePlotPlanApi";

export type PlotPlanEditorProps = {
  readonly editPlotPlan?: PlotPlan;
  readonly onClose: () => void;
};

export const PlotPlanEditor: FC<PlotPlanEditorProps> = ({ editPlotPlan, onClose }) => {
  const { t } = useTranslation();
  const { fetchPlotPlan, createPlotPlan, updatePlotPlan } = usePlotPlanApi();

  const mode: "create" | "update" = editPlotPlan === undefined ? "create" : "update";

  const formSchema = z.object({
    plot_plan_type: z.enum([String(PlotPlanType.DETAIL), String(PlotPlanType.OVERVIEW)]),
    plot_plan_name: z
      .string()
      .min(1, { message: t("input_plot_plan_name_validation") })
      .max(100, { message: t("plot_plan_name_is_too_long_100") }),
    uploadfile: z.z
      .instanceof(FileList)
      .nullish()
      .refine((file) => mode === "update" || !!file, { message: t("select_floor_plan_validation") }),
    quality: z.string(),
    note: z.string().max(500, { message: t("note_is_too_long_500") }),
  });
  type FormSchema = z.infer<typeof formSchema>;
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    reValidateMode: "onSubmit",
    defaultValues: {
      plot_plan_type: String(editPlotPlan?.plot_plan_type ?? PlotPlanType.DETAIL),
      plot_plan_name: "",
      uploadfile: null,
      quality: mode === "create" ? qualityMasters.find((quality) => quality.id === "middle").value.toString() : null,
      note: "",
    },
  });
  const { onChange: onFileChange } = register("uploadfile");
  const [loading, setLoading] = useState<boolean>(editPlotPlan !== undefined);
  const [updateTargetId, setUpdateTargetId] = useState<number>();
  const [fileName, setFileName] = useState<string>();
  const [timestamp, setTimestamp] = useState<TimeStamp>();

  const refresh = async () => {
    const data = await fetchPlotPlan(editPlotPlan.plot_plan_id);
    if (data) {
      const { file_name, plot_plan_id, plot_plan_type, plot_plan_name, quality, note } = data;
      setUpdateTargetId(plot_plan_id);
      setTimestamp(data.timestamp);

      reset({ plot_plan_type: String(plot_plan_type), plot_plan_name, quality: quality.toString(), note });
      setFileName(file_name);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "update") {
      refresh();
    }
  }, []);

  const handleImageUpload = (event) => {
    setFileName(event.target.files[0]?.name);
    onFileChange(event);
  };

  const onSubmit = useCallback(
    async ({ plot_plan_type, plot_plan_name, uploadfile, quality, note }: FormSchema) => {
      setLoading(true);
      try {
        let response;
        if (mode === "create") {
          response = await createPlotPlan({
            plot_plan_name,
            uploadfile: uploadfile?.[0],
            quality: Number(quality),
            note,
            plot_plan_type: Number(plot_plan_type) as PlotPlanTypeType,
          });
        } else {
          response = await updatePlotPlan(
            {
              plot_plan_id: updateTargetId,
              plot_plan_name,
              quality: Number(quality),
              note,
              plot_plan_type: Number(plot_plan_type) as PlotPlanTypeType,
              timestamp,
            },
            () => refresh()
          );
        }

        if (response) {
          onClose();
        }
      } finally {
        setLoading(false);
      }
    },
    [updateTargetId, timestamp, createPlotPlan, updatePlotPlan, mode, onClose, refresh]
  );

  return (
    <Modal onClose={onClose}>
      <form>
        <ModalHeader>{t("edit_plot_plan")}</ModalHeader>
        <ModalBody>
          <div>
            <FormRow className="mt-0">
              <FormLabel>{t("plot_plan_type")}</FormLabel>
              <Controller
                control={control}
                name="plot_plan_type"
                render={({ field: { value, onChange } }) => (
                  <ToggleWithWords
                    id="plot-plan-edit-map-type"
                    onChange={(_, v) => onChange(v ? String(PlotPlanType.DETAIL) : String(PlotPlanType.OVERVIEW))}
                    isDefaultOn={value === String(PlotPlanType.DETAIL)}
                    labelOn={t("map_detail")}
                    labelOff={t("map_overview")}
                    disabled={mode === "update"}
                  />
                )}
              />
            </FormRow>
            <FormRow errorComponent={<FormError>{errors.plot_plan_name?.message}</FormError>}>
              <FormLabel>{t("plot_plan_name")}</FormLabel>
              <InputText {...register("plot_plan_name")} />
            </FormRow>
            <FormRow errorComponent={<FormError>{errors.uploadfile?.message}</FormError>}>
              <FormLabel>{t("floor_plan")}</FormLabel>
              {editPlotPlan ? (
                !loading && <div>{fileName}</div>
              ) : (
                <>
                  <FileUploader className="min-w-[110px]" {...register("uploadfile")} onChange={handleImageUpload} />
                  {fileName && <div className="ml-5">{fileName}</div>}
                </>
              )}
            </FormRow>
            <FormRow>
              <FormLabel>{t("image_quality")}</FormLabel>
              <div className="flex">
                {qualityMasters.map((quality) => (
                  <Radio key={quality.id} id={quality.id} value={quality.value} {...register("quality")}>
                    {t(quality.label)}
                  </Radio>
                ))}
              </div>
            </FormRow>
            <FormRow
              className="items-start"
              errorComponent={errors.note && <FormError>{errors.note.message}</FormError>}
            >
              <FormLabel className="mt-[4px]">{t("note")}</FormLabel>
              <TextArea {...register("note")} />
            </FormRow>
            {/* プロットプラン名のinput textでEnterを押したときにsubmitされるのを防ぐため、非表示のinput textを配置 #472 */}
            {/* ref. https://kasumiblog.org/form-enter/ */}
            <input type="text" className="hidden" />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button buttonType="cancel" onClick={onClose} disabled={loading}>
            {t("cancel")}
          </Button>
          <SubmitButton onClick={handleSubmit(onSubmit)} loading={loading} layout={true}>
            {t("save")}
          </SubmitButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};
